import type { Permission } from '@/shared/constants';
import type { NavNode } from '@/shared/ui/sidebar';
import type { AppNavNode } from './types';

export type NavFilterContext = {
	permissions?: Set<Permission>;
	rolePermissions?: Set<Permission>;
	companyPermissions?: Set<Permission>;
	isDelegation?: boolean | null;
	isSuperUser?: boolean;
};

const hasPermissionAccess = (
	context: Pick<NavFilterContext, 'permissions' | 'rolePermissions'>,
	requiredPermission?: Permission[],
	mode: 'combined' | 'role-only' = 'combined',
) => {
	if (!requiredPermission || requiredPermission.length === 0) return true;
	const source = mode === 'role-only' ? context.rolePermissions : context.permissions;
	if (!source) return false;
	return requiredPermission.every((p) => source.has(p));
};

const hasBuyAccess = (
	companyPermissions: NavFilterContext['companyPermissions'],
	requiredBuyPermission?: Permission,
) => {
	if (!requiredBuyPermission) return true;
	if (!companyPermissions) return false;
	return companyPermissions.has(requiredBuyPermission);
};

const toNavNode = (n: AppNavNode, children?: NavNode[]): NavNode => {
	if (n.type === 'group') {
		return { type: 'group', text: n.text, icon: n.icon, children: children ?? [] };
	}
	return {
		type: 'link',
		text: n.text,
		to: n.to,
		tone: n.tone,
		icon: n.icon,
		disabled: n.disabled,
		useIndicator: n.useIndicator,
	};
};

export const filterNav = (nodes: AppNavNode[], context: NavFilterContext): NavNode[] => {
	const { isDelegation, companyPermissions, isSuperUser } = context;
	return nodes
		.filter(
			(n) =>
				isSuperUser ||
				(hasPermissionAccess(context, n.requiredPermission, n.requiredPermissionMode) &&
					hasBuyAccess(companyPermissions, n.requiredBuyPermission) &&
					!(n.hiddenForDelegation && isDelegation)),
		)
		.map((n): NavNode | null => {
			if (n.type === 'group') {
				const children = filterNav(n.children, context);
				if (children.length === 0) return null;
				return toNavNode(n, children);
			}
			return toNavNode(n);
		})
		.filter(Boolean) as NavNode[];
};
